// 261025 E3-P3f — hàm THUẦN của «Duyệt & đẩy N tập» (đẩy sang Chapters THEO DANH SÁCH TẬP): tick ở Sổ duyệt, dải N tập, xác nhận đẩy một phần, nhãn kết quả F5, lỗi 409.
// KHÔNG import antd / react (jest của react-scripts không load được antd v6). `.tsx`/hook chỉ vẽ + gọi API.
// Máy chủ vẫn là cổng thật (`handoffEpisodeEligibility`): «Đạt» và `eligibleForPush` chỉ là luật CHỌN của FE.
import { friendlyApiMessage } from './jobErrorText';
import type {
  HandoffBatchCode, HandoffBatchEpisodeResult, HandoffBatchPreview, HandoffSkipCode, WorkbenchEpisode, WorkbenchHandoffBatchView,
} from '../../../types/adaptationWorkbench';
import type { PreviewOverlay } from './skipReasonView';
import { PREVIEW_ERROR_LINE } from './skipReasonView';

// ─── Nhãn F5 (tiếng người) ─────────────────────────────────────────────────

export type BatchTone = 'ok' | 'warn' | 'bad' | 'muted';

export const BATCH_CODE_LABEL: Record<HandoffBatchCode, { label: string; tone: BatchTone }> = {
  created: { label: 'Đã tạo chương', tone: 'ok' },
  updated: { label: 'Đã cập nhật chương', tone: 'ok' },
  unchanged: { label: 'Không đổi', tone: 'muted' },
  kept_for_plan: { label: 'Giữ nguyên — đã có Film plan', tone: 'muted' },
  kept_edited: { label: 'Giữ nguyên — bạn đã sửa tay', tone: 'warn' },
  skipped_not_eligible: { label: 'Bỏ qua — chưa đủ điều kiện', tone: 'warn' },
  skipped_script_changed: { label: 'Bỏ qua — tập đã đổi', tone: 'warn' },
  cancelled: { label: 'Chưa đẩy — đã dừng', tone: 'muted' },
  failed: { label: 'Lỗi', tone: 'bad' },
};
export const batchCodeLabel = (c: string | null | undefined): { label: string; tone: BatchTone } =>
  BATCH_CODE_LABEL[c as HandoffBatchCode] ?? { label: 'Chưa rõ', tone: 'muted' };

export const SKIP_CODE_LABEL: Record<HandoffSkipCode, string> = {
  NOT_DRAFT: 'chưa có kịch bản nháp',
  NOT_IN_OUTLINE: 'không nằm trong danh sách tập',
  CRITIC_UNRESOLVED: 'còn lỗi nặng giám khảo chưa sửa',
  SOURCE_NAME_UNRESOLVED: 'còn tên gốc không tự thay được',
  RANGE_NOT_READY: 'tập đứng trước chưa sẵn sàng (nối tập)',
  READER_BLOCKING: 'AI đọc cả mùa chặn tập này',
};
export const skipCodeLabel = (c: string | null | undefined): string => SKIP_CODE_LABEL[c as HandoffSkipCode] ?? 'chưa đủ điều kiện';

/** Một dòng kết quả cho hộp: nhãn + lý do (câu của máy chủ nếu có, không thì nhãn mã lý do). Không lộ mã kỹ thuật. */
export interface BatchResultRow { ep: number; label: string; tone: BatchTone; detail: string | null; leaks: string | null }
export const batchResultRows = (results: ReadonlyArray<HandoffBatchEpisodeResult> | null | undefined): BatchResultRow[] =>
  [...(results ?? [])].sort((a, b) => a.episode - b.episode).map((r) => {
    const { label, tone } = batchCodeLabel(r.code);
    const detail = r.reason?.trim() || (r.skipCode ? skipCodeLabel(r.skipCode) : null);
    const leaks = (r.leaks ?? []).length ? (r.leaks ?? []).map((l) => `${l.term} ×${l.count}`).join(', ') : null;
    return { ep: r.episode, label, tone, detail, leaks };
  });

// ─── Số đếm / dòng «tạo x · cập nhật y · giữ z» ───────────────────────────

export interface PlanCounts { create: number; update: number; keep: number; unchanged: number; skipped: number }
const KEEP: ReadonlySet<string> = new Set(['kept_for_plan', 'kept_edited']);
const SKIP: ReadonlySet<string> = new Set(['skipped_not_eligible', 'skipped_script_changed']);

export const planCountsOf = (episodes: ReadonlyArray<{ action: string }> | null | undefined): PlanCounts => {
  const c: PlanCounts = { create: 0, update: 0, keep: 0, unchanged: 0, skipped: 0 };
  for (const e of episodes ?? []) {
    if (e.action === 'created') c.create++;
    else if (e.action === 'updated') c.update++;
    else if (KEEP.has(e.action)) c.keep++;
    else if (e.action === 'unchanged') c.unchanged++;
    else if (SKIP.has(e.action)) c.skipped++;
  }
  return c;
};

/** «tạo x · cập nhật y · giữ z» (+ «· bỏ qua k»); bỏ phần bằng 0; không có gì để nói ⇒ ''. */
export const planLine = (c: PlanCounts): string => {
  const parts: string[] = [];
  if (c.create) parts.push(`tạo ${c.create}`);
  if (c.update) parts.push(`cập nhật ${c.update}`);
  if (c.keep) parts.push(`giữ ${c.keep}`);
  if (c.skipped) parts.push(`bỏ qua ${c.skipped}`);
  return parts.join(' · ');
};

// ─── Chọn tập (Sổ duyệt) ───────────────────────────────────────────────────

/** Phần của một hàng Sổ duyệt mà luật tick cần (`ReviewRow` thoả mãn). */
export interface TickRow { ep: number; groups: ReadonlyArray<string>; written: string; pushable?: boolean; contentSha?: string; previewOverlay?: PreviewOverlay }

/** Tập đẩy được theo workbench: máy chủ nói đẩy được (BE mới) VÀ có băm nội dung để gửi lại. BE cũ (không có cờ) ⇒ false ⇒ Sổ duyệt giữ đường đẩy cũ. */
export const isPushable = (ep: Pick<WorkbenchEpisode, 'written' | 'eligibleForPush' | 'contentSha'>): boolean =>
  ep.written === 'draft' && ep.eligibleForPush === true && !!ep.contentSha;

/** Luật tick: chỉ tập «Đạt» (nhóm `pass`: không cần bạn xem, chưa đẩy/cần đẩy lại) VÀ đẩy được. */
export const canTick = (r: TickRow): boolean => !r.previewOverlay && r.groups.includes('pass') && r.pushable === true && !!r.contentSha;

/** Tooltip «vì sao không tick được» (null = tick được). */
export const tickOffReason = (r: TickRow): string | null => {
  if (canTick(r)) return null;
  if (r.previewOverlay?.kind === 'scriptChanged') return 'Tập đã đổi từ lúc bạn xem — tải lại';
  if (r.previewOverlay?.kind === 'notEligible') return r.previewOverlay.reason;
  if (r.written === 'none') return 'Tập chưa viết';
  if (r.written === 'failed') return 'Tập viết hỏng — viết lại trước';
  if (r.groups.includes('need')) return 'Còn việc cần bạn xem — xử lý rồi mới đẩy cùng nhóm «Đạt»';
  if (r.groups.includes('pushed')) return 'Đã đẩy, Chapters đang khớp bản này';
  if (!r.groups.includes('pass')) return 'Chưa đạt';
  if (r.pushable !== true) return 'Máy chủ chưa cho đẩy tập này (khung xương chưa duyệt, danh sách tập cũ hoặc tập trước còn lỗi nối tập)';
  return 'Thiếu băm nội dung — tải lại trang';
};

export const tickableEpisodes = (rows: ReadonlyArray<TickRow>): number[] => rows.filter(canTick).map((r) => r.ep).sort((a, b) => a - b);

export const toggleTick = (sel: ReadonlySet<number>, ep: number, rows: ReadonlyArray<TickRow>): Set<number> => {
  const next = new Set(sel);
  if (next.has(ep)) next.delete(ep);
  else if (rows.some((r) => r.ep === ep && canTick(r))) next.add(ep);
  return next;
};
export const selectAllTickable = (rows: ReadonlyArray<TickRow>): Set<number> => new Set(tickableEpisodes(rows));
/** Bỏ khỏi lựa chọn tập không còn tick được (dữ liệu vừa đổi: đã đẩy xong, có lỗi mới…). */
export const pruneSelection = (sel: ReadonlySet<number>, rows: ReadonlyArray<TickRow>): Set<number> => {
  const ok = new Set(tickableEpisodes(rows));
  return new Set(Array.from(sel).filter((e) => ok.has(e)));
};
export type HeaderTick = 'none' | 'some' | 'all' | 'disabled';
export const headerTick = (sel: ReadonlySet<number>, rows: ReadonlyArray<TickRow>): HeaderTick => {
  const all = tickableEpisodes(rows);
  if (!all.length) return 'disabled';
  const n = all.filter((e) => sel.has(e)).length;
  return n === 0 ? 'none' : n === all.length ? 'all' : 'some';
};

/** Phần gửi lên: `{episode, contentSha}` của đúng các tập được chọn, theo workbench hiện tại. Tập thiếu băm bị bỏ (không gửi băm bịa). */
export const batchItemsOf = (episodes: ReadonlyArray<Pick<WorkbenchEpisode, 'ep' | 'contentSha'>>, numbers: ReadonlyArray<number>): Array<{ episode: number; contentSha: string }> => {
  const want = new Set(numbers);
  return episodes.filter((e) => want.has(e.ep) && !!e.contentSha).map((e) => ({ episode: e.ep, contentSha: e.contentSha as string })).sort((a, b) => a.episode - b.episode);
};

// ─── Thanh dính + xác nhận đẩy một phần ───────────────────────────────────

export interface StickyView { visible: boolean; label: string; line: string; partial: boolean; n: number }
/**
 * Thanh dính ở Sổ duyệt: «Duyệt & đẩy N tập · tạo x · cập nhật y». N = số tập ĐÃ TICK. `preview` null = chưa có số (đang tải / lỗi) ⇒ chỉ nêu N.
 * `partial` ⇔ còn tập đẩy được (tick được hoặc đã tick hợp lệ) mà không được chọn — khi đó bấm phải qua hộp xác nhận.
 */
export const stickyView = (sel: ReadonlySet<number>, rows: ReadonlyArray<TickRow>, preview: PlanCounts | null): StickyView => {
  const tickable = tickableEpisodes(rows);
  const n = tickable.filter((e) => sel.has(e)).length;
  if (n === 0) return { visible: false, label: '', line: '', partial: false, n: 0 };
  const line = preview ? planLine(preview) : '';
  return { visible: true, label: `Duyệt & đẩy ${n} tập${line ? ` · ${line}` : ''}`, line, partial: n < tickable.length, n };
};

export interface PartialConfirm { title: string; content: string; okText: string }
/** Hộp xác nhận đẩy một phần: nêu số tập đẩy / số tập còn lại và kế hoạch. */
export const partialConfirmOf = (selected: number, tickable: number, preview: PlanCounts | null): PartialConfirm => {
  const rest = Math.max(0, tickable - selected);
  const plan = preview ? planLine(preview) : '';
  return {
    title: `Chỉ đẩy ${selected}/${tickable} tập «Đạt»?`,
    content: `Sẽ đẩy ${selected} tập đã chọn${plan ? ` (${plan})` : ''}. ${rest} tập «Đạt» còn lại chưa được đẩy — bạn đẩy sau cũng được.`,
    okText: `Đẩy ${selected} tập`,
  };
};

// ─── Dải: nút «Duyệt & đẩy N tập» ──────────────────────────────────────────

export type PreviewState =
  | { status: 'idle' | 'loading' | 'error' }
  | { status: 'ok'; data: Pick<HandoffBatchPreview, 'episodes'> };

/** `calculating` (3h5a): xem thử đẩy chưa về (đang tính / chưa bắt đầu) — nút MỜ, KHÔNG phải nút đỏ bấm được. */
export type StripMode = 'legacy' | 'running' | 'none' | 'allPushed' | 'partial' | 'nothing' | 'ready' | 'calculating';
export interface PushStrip {
  mode: StripMode;
  /** Số tập sẽ GHI (tạo/cập nhật). */
  n: number;
  /** Đúng danh sách tập gửi lên (đã lọc theo xem thử). */
  episodes: number[];
  label: string;
  disabled: boolean;
  disabledReason: string | null;
  /** «tạo x · cập nhật y · giữ z». */
  line: string | null;
  /** Câu tiến độ khi đang đẩy. */
  progress: string | null;
}

export const batchProgressText = (b: Pick<WorkbenchHandoffBatchView, 'done' | 'total'> | null | undefined): string =>
  b ? `Đang đẩy ${b.done}/${b.total} tập` : '';

const CALCULATING_TEXT = 'đang tính kế hoạch…';
const strip = (p: Partial<PushStrip> & Pick<PushStrip, 'mode' | 'label'>): PushStrip => ({ n: 0, episodes: [], disabled: false, disabledReason: null, line: null, progress: null, ...p });

/**
 * Nút đỏ của dải Chặng 3. N = tập qua cổng RẺ (`eligibleForPush`) mà xem thử không ra `unchanged`/bỏ qua; kept_* hiện ở dòng «giữ z», KHÔNG tính vào N (đẩy chúng không ghi gì — nếu tính, nút không bao giờ về «Đã đẩy hết»).
 * N = 0 ⇒ «Đã đẩy hết», nút xám, không gửi. BE cũ (không tập nào có `eligibleForPush`) ⇒ `legacy` (dải giữ đường «Đẩy sang Chapters» cũ).
 */
export const pushStripOf = (
  episodes: ReadonlyArray<Pick<WorkbenchEpisode, 'ep' | 'written' | 'eligibleForPush' | 'contentSha'> & { handoff?: string }> | null | undefined,
  batch: Pick<WorkbenchHandoffBatchView, 'status' | 'done' | 'total'> | null | undefined,
  preview: PreviewState,
): PushStrip => {
  const eps = episodes ?? [];
  if (eps.length === 0 || eps.every((e) => e.eligibleForPush === undefined)) return strip({ mode: 'legacy', label: 'Đẩy sang Chapters' });
  if (batch?.status === 'RUNNING') {
    return strip({ mode: 'running', label: batchProgressText(batch), disabled: true, disabledReason: 'Đang đẩy ở nền — chờ xong', progress: batchProgressText(batch) });
  }
  const elig = eps.filter(isPushable).map((e) => e.ep).sort((a, b) => a - b);
  // 3h1 (H11a): «Đã đẩy hết» chỉ đúng khi MỌI tập đã viết đều đã đẩy. Tập bị chặn (không `isPushable`) và chưa khớp Chapters ⇒ «Đã đẩy N tập · M tập chưa đẩy được».
  const written = eps.filter((e) => e.written === 'draft');
  const pushedN = written.filter((e) => (e as { handoff?: string }).handoff === 'up_to_date').length;
  const stuckN = written.filter((e) => (e as { handoff?: string }).handoff !== 'up_to_date' && !isPushable(e)).length;
  const partialStrip = (line: string | null = null) => strip({
    mode: 'partial', label: `Đã đẩy ${pushedN} tập · ${stuckN} tập chưa đẩy được — xem Sổ duyệt`, disabled: true,
    disabledReason: 'Các tập còn lại đang bị chặn (lỗi nặng, nối tập…) — lý do từng tập có ở Sổ duyệt', line,
  });
  if (elig.length === 0) {
    if (pushedN > 0 && stuckN > 0) return partialStrip();
    return strip({ mode: 'none', label: 'Duyệt & đẩy', disabled: true, disabledReason: 'Chưa có tập nào đủ điều kiện đẩy (cần kịch bản đã viết, khung xương đã duyệt, không lỗi nặng, nối tập sạch)' });
  }
  if (preview.status !== 'ok') {
    const label = `Duyệt & đẩy ${elig.length} tập`;
    // 3h5a (NIT): xem thử đang tính ⇒ nút mờ (không gửi tập nào, không giữ nút đỏ); xem thử LỖI ⇒ vẫn bấm được (máy chủ kiểm lại khi đẩy).
    if (preview.status === 'loading' || preview.status === 'idle') return strip({ mode: 'calculating', n: elig.length, label, disabled: true, disabledReason: CALCULATING_TEXT, line: CALCULATING_TEXT });
    return strip({ mode: 'ready', n: elig.length, episodes: elig, label, line: PREVIEW_ERROR_LINE });
  }
  const byEp = new Map(preview.data.episodes.map((e) => [e.episode, e.action as string]));
  const writes = elig.filter((e) => { const a = byEp.get(e); return a === 'created' || a === 'updated'; });
  const counts = planCountsOf(elig.map((e) => ({ action: byEp.get(e) ?? 'unchanged' })));
  const line = planLine(counts) || null;
  if (writes.length === 0) {
    if (counts.skipped > 0) return strip({ mode: 'nothing', label: 'Không có tập nào đẩy được', disabled: true, disabledReason: 'Mọi tập còn lại bị máy chủ bỏ qua — xem lý do ở Sổ duyệt', line });
    if (stuckN > 0) return partialStrip(line);
    return strip({ mode: 'allPushed', label: 'Đã đẩy hết', disabled: true, disabledReason: 'Chapters đã khớp mọi tập đủ điều kiện', line });
  }
  return strip({ mode: 'ready', n: writes.length, episodes: writes, label: `Duyệt & đẩy ${writes.length} tập`, line });
};

// ─── Kết quả lô ────────────────────────────────────────────────────────────

/** Câu tóm tắt khi lô xong. `warning` khi có tập lỗi / bỏ qua / bị dừng / giữ bản bạn đã sửa. */
export const batchOutcomeOf = (b: Pick<WorkbenchHandoffBatchView, 'status' | 'counts' | 'total' | 'note' | 'cancelRequested'> | null | undefined): { level: 'success' | 'warning'; text: string } | null => {
  if (!b || b.status === 'RUNNING') return null;
  const c = b.counts;
  const parts: string[] = [];
  const add = (n: number | undefined, t: string) => { if (n) parts.push(`${t} ${n}`); };
  add(c?.created, 'tạo'); add(c?.updated, 'cập nhật'); add(c?.unchanged, 'không đổi');
  add((c?.kept_for_plan ?? 0) + (c?.kept_edited ?? 0), 'giữ nguyên');
  add((c?.skipped_not_eligible ?? 0) + (c?.skipped_script_changed ?? 0), 'bỏ qua');
  add(c?.cancelled, 'chưa đẩy (đã dừng)'); add(c?.failed, 'lỗi');
  const head = b.status === 'FAILED' ? 'Việc đẩy dừng giữa chừng' : b.cancelRequested || (c?.cancelled ?? 0) > 0 ? 'Đã dừng đẩy' : `Đã đẩy ${b.total} tập`;
  const warn = b.status === 'FAILED' || (c?.failed ?? 0) > 0 || (c?.cancelled ?? 0) > 0 || (c?.skipped_not_eligible ?? 0) > 0 || (c?.skipped_script_changed ?? 0) > 0 || (c?.kept_edited ?? 0) > 0;
  return { level: warn ? 'warning' : 'success', text: `${head}${parts.length ? ` — ${parts.join(' · ')}` : ''}${b.note ? ` · ${b.note}` : ''}` };
};

/** Lô vừa chạy xong mà phiên này đã bấm (so `jobId` đang theo dõi) ⇒ mở hộp kết quả đúng một lần. */
export const shouldOpenResults = (watchJobId: string | null, batch: Pick<WorkbenchHandoffBatchView, 'jobId' | 'status'> | null | undefined): boolean =>
  !!watchJobId && !!batch && batch.jobId === watchJobId && batch.status !== 'RUNNING';

// ─── Lỗi 409 / 400 của POST ────────────────────────────────────────────────

export interface BatchErrorView {
  kind: 'changed' | 'running' | 'blocked' | 'network' | 'other';
  text: string;
  /** Làm mới workbench sau khi báo. */
  reload: boolean;
  /** `HANDOFF_RUNNING`: bám theo việc đang chạy. */
  jobId?: string;
  /** `SCRIPT_CHANGED`: các tập đã đổi. */
  episodes?: number[];
}
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

export const batchErrorView = (err: unknown): BatchErrorView => {
  const resp = isObj(err) && isObj(err.response) ? err.response : null;
  if (!resp) return { kind: 'network', text: 'Không kết nối được máy chủ — thử lại sau', reload: false };
  const data = isObj(resp.data) ? resp.data : {};
  const code = typeof data.code === 'string' ? data.code : '';
  const msg = typeof data.message === 'string' ? data.message : '';
  switch (code) {
    case 'SCRIPT_CHANGED': {
      const eps = Array.isArray(data.episodes) ? (data.episodes as unknown[]).filter((x): x is number => typeof x === 'number') : [];
      return { kind: 'changed', text: `Tập ${eps.length ? eps.join(', ') : 'đã chọn'} vừa đổi từ lúc bạn xem — đã tải lại, chọn lại rồi đẩy`, reload: true, episodes: eps };
    }
    case 'HANDOFF_RUNNING':
      return { kind: 'running', text: 'Đang có một lần đẩy chạy ở nền — theo dõi nó ở dải/Sổ duyệt', reload: true, ...(typeof data.jobId === 'string' ? { jobId: data.jobId } : {}) };
    case 'SCRIPT_JOB_RUNNING': return { kind: 'blocked', text: 'Truyện đang có việc viết/đọc kịch bản chạy — chờ xong rồi đẩy', reload: true };
    case 'JOB_RUNNING': return { kind: 'blocked', text: 'Truyện đang bản địa hoá hoặc dựng khung xương — chờ xong rồi đẩy', reload: true };
    case 'APPEND_RUN_ACTIVE': return { kind: 'blocked', text: 'Chuỗi tự chạy của truyện còn đang chạy — chờ chuỗi dừng/xong rồi đẩy', reload: true };
    case 'OUTLINE_MISSING': return { kind: 'blocked', text: 'Chưa có khung xương', reload: true };
    case 'OUTLINE_NOT_APPROVED': return { kind: 'blocked', text: 'Khung xương chưa được duyệt', reload: true };
    case 'NO_DRAFT_SCRIPTS': return { kind: 'blocked', text: 'Chưa có kịch bản nháp nào để đẩy', reload: true };
    case 'EPISODES_STALE': return { kind: 'blocked', text: 'Danh sách tập đã cũ — chia lại tập rồi đẩy', reload: true };
    case 'SEASON_READER_REQUIRED': return { kind: 'blocked', text: 'Chưa có kết quả AI đọc cả mùa — chạy người đọc ở ⑤ rồi đẩy', reload: true };
    case 'SEASON_READER_STALE': return { kind: 'blocked', text: 'Kịch bản đã đổi sau lần AI đọc cả mùa — đọc lại rồi đẩy', reload: true };
    case 'SEASON_READER_RUNNING': return { kind: 'blocked', text: 'AI đang đọc cả mùa — chờ xong rồi đẩy', reload: true };
    case 'STEP_GATE_BLOCKED': return { kind: 'blocked', text: 'Cổng AI đọc cả mùa đang chặn — xử lý lỗi liên tập hoặc bấm “Vẫn tiếp tục” ở khối người đọc', reload: true };
    case 'INVALID_HANDOFF_BATCH': return { kind: 'other', text: 'Danh sách tập gửi lên không hợp lệ — tải lại trang rồi thử lại', reload: true };
    default: return { kind: 'other', text: friendlyApiMessage(msg, 'Không đẩy được — thử lại sau'), reload: false };
  }
};

/** Lỗi huỷ: 409 `HANDOFF_NOT_RUNNING` = đã xong rồi (không phải lỗi). */
export const cancelErrorText = (err: unknown): string => {
  const code = isObj(err) && isObj(err.response) && isObj(err.response.data) ? err.response.data.code : undefined;
  if (code === 'HANDOFF_NOT_RUNNING') return 'Việc đẩy đã kết thúc — không còn gì để dừng';
  if (code === 'HANDOFF_JOB_NOT_FOUND') return 'Không tìm thấy việc đẩy này';
  return 'Chưa gửi được lệnh dừng — thử lại';
};

// ─── 3h3 (K12) — thanh dính đáy ⑤ «Đẩy N tập sang Chapters · tạo x · cập nhật y · giữ nguyên z» ─────────

export interface PushBarView {
  /** Có tập đã viết thì mới có thanh (trước đó chưa có gì để đẩy). */
  visible: boolean;
  /** Câu bên trái. */
  text: string;
  /** Chú thích nhỏ cạnh nút («đẩy tay»). */
  hint: string | null;
  button: { label: string; disabled: boolean; reason: string | null };
  /** Bấm thì đẩy đúng các tập này theo lô (đường «Duyệt & đẩy N tập»); rỗng ⇒ đường cũ (BE cũ) mở hộp Đẩy. */
  episodes: number[];
  legacy: boolean;
}

/** Từ `pushStripOf` (cùng nguồn với dải) — một luật cho dải và thanh đáy ⑤. Không số tiền. */
export const pushBarOf = (push: PushStrip, episodes: ReadonlyArray<Pick<WorkbenchEpisode, 'written' | 'handoff'>> | null | undefined): PushBarView => {
  const written = (episodes ?? []).filter((e) => e.written === 'draft');
  const pushed = written.filter((e) => (e as { handoff?: string }).handoff === 'up_to_date').length;
  const base = { visible: written.length > 0, episodes: push.episodes, legacy: push.mode === 'legacy' };
  switch (push.mode) {
    case 'legacy': return { ...base, text: 'Đẩy các tập đã viết sang Chapters', hint: null, button: { label: 'Đẩy sang Chapters…', disabled: false, reason: null } };
    case 'ready': return { ...base, text: `Đẩy ${push.n} tập sang Chapters${push.line ? ` · ${push.line}` : ''}`, hint: 'đẩy tay', button: { label: `Đẩy ${push.n} tập…`, disabled: false, reason: null } };
    case 'calculating': return { ...base, text: `${push.label} · ${CALCULATING_TEXT}`, hint: null, button: { label: 'Đẩy sang Chapters…', disabled: true, reason: push.disabledReason } };
    case 'running': return { ...base, text: push.label, hint: null, button: { label: 'Đang đẩy…', disabled: true, reason: push.disabledReason } };
    case 'allPushed': return { ...base, text: `Đã đẩy ${pushed}/${written.length} tập · Chapters khớp kịch bản.`, hint: null, button: { label: 'Đẩy sang Chapters…', disabled: true, reason: push.disabledReason } };
    case 'partial': return { ...base, text: push.label, hint: null, button: { label: 'Đẩy sang Chapters…', disabled: true, reason: push.disabledReason } };
    default: return { ...base, text: push.label, hint: null, button: { label: 'Đẩy sang Chapters…', disabled: true, reason: push.disabledReason } };   // none / nothing
  }
};
